import { details, div, span, summary, type Mountable, type Renderable } from '@llui/dom';

import { chevronRightIcon } from './icon.js';

/**
 * `<Collapse>` — disclosure / accordion primitive wrapping native
 * `<details>` / `<summary>`.
 *
 * Why native over headless: `<details>` already gives us keyboard
 * conformant toggling (Enter / Space on the summary), correct ARIA
 * (implicit `aria-expanded` + `role='group'` on the details element),
 * and zero JS. The headless `@llui/components/accordion` is great for
 * exclusive-open accordion groups with state-machine coordination,
 * but for an independently-toggled disclosure widget that's overkill.
 *
 * What the atom owns:
 *  - The `<details>` / `<summary>` boilerplate.
 *  - A token-correct visual treatment: card chrome on the outer
 *    container, surface-sunken hover on the summary, a rotating
 *    Lucide `chevron-right` caret (rotated 90° when open) and
 *    suppression of the native/WebKit marker.
 *  - `openByDefault` mounting via the native `open` attribute.
 *  - A `panelClass` slot for the body (which the caller fills with
 *    list items, prose, an inner card, whatever).
 *
 * What the caller still owns:
 *  - The summary content (title, icon, badge, count chip — anything).
 *  - The panel content (the disclosed body).
 *  - Any per-section data attributes for testing.
 *
 * Construction:
 *
 *   collapse({
 *     openByDefault: true,
 *     summary: [span({class: 'icon'}, ['📍']), text('Locations')],
 *     children: [ul([...])],
 *     testid: 'entity-root-section',
 *   })
 *
 * The summary `<summary>` is rendered with `list-style: none` so the
 * caller can ship a custom caret (the atom's CSS handles this).
 */

export interface CollapseOptions {
  /** Whether the section is open on first mount. */
  openByDefault?: boolean;
  /** Inline contents of the summary row (title + chips + counts). */
  summary: Renderable;
  /** Inline contents of the disclosure body. */
  children: Renderable;
  /** Optional CSS class for app-specific extension on the root. */
  className?: string;
  /** Optional CSS class for the summary row. */
  summaryClass?: string;
  /** Optional CSS class for the body wrapper. */
  panelClass?: string;
  /** Forwarded `data-testid` on the root `<details>`. */
  testid?: string;
  /** Forwarded `data-*` slot — pass any per-section attrs the caller needs. */
  dataAttrs?: Record<string, string>;
}

export function collapse(opts: CollapseOptions): Mountable {
  const rootClass = `dl-collapse${opts.className ? ` ${opts.className}` : ''}`;
  const summaryKlass = `dl-collapse-summary${opts.summaryClass ? ` ${opts.summaryClass}` : ''}`;
  const panelKlass = `dl-collapse-panel${opts.panelClass ? ` ${opts.panelClass}` : ''}`;

  // Build the props object — the data attrs spread comes last so the
  // caller can override class/testid if they really want to (they
  // shouldn't, but the door is open).
  const rootProps = {
    class: rootClass,
    'data-testid': opts.testid,
    ...(opts.openByDefault ? { open: true } : {}),
    ...(opts.dataAttrs ?? {}),
  };

  return details(rootProps, [
    // The caret is a real element (not `::before` content) so it can be a
    // proper Lucide chevron rather than a `▸` text character — the glyph
    // inherited the body font's weight and baseline and never matched the
    // icons sitting beside it in the summary row. Decorative: the summary's
    // implicit `aria-expanded` is what screen readers announce.
    summary({ class: summaryKlass }, [
      span({ class: 'dl-collapse-caret', 'aria-hidden': 'true' }, [chevronRightIcon({ size: 16 })]),
      ...(Array.isArray(opts.summary) ? opts.summary : [opts.summary]),
    ]),
    div({ class: panelKlass }, opts.children),
  ]);
}
